import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components";

import CheckIcon from "../../../../../../icons/Check";
import { CLOUDFLARE_PROXY_HELP_URL, CLOUDFLARE_PROXY_ISSUE, DnsRecord } from "../../../../../../services/customDomain";
import { classNames } from "../../../../../../utils/styles";

// "<anchor>" in the copy (not "<link>", a void HTML tag the parser would close empty)
const ExternalLink = ({ href, children }: { href?: string; children?: React.ReactNode }) => (
  <a href={href} target="_blank" rel="noreferrer">
    {children}
  </a>
);

const ring = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 122, 0, 0.6);
  }
  100% {
    box-shadow: 0 0 0 8px rgba(255, 122, 0, 0);
  }
`;

const breathe = keyframes`
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.55;
  }
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Item = styled.li<{ $verified: boolean }>`
  display: grid;
  grid-template-columns: 7rem minmax(0, 1fr);
  gap: 0.5rem 1rem;
  align-items: center;
  padding: 1rem 20px;
  background: ${props => (props.$verified ? "#343949" : "var(--color-neutral-dark)")};
  border-radius: 12px;
  transition: background-color 0.2s;

  dt {
    font-family: Inter, sans-serif;
    font-size: 0.6875rem;
    font-weight: 800;
    line-height: 1rem;
    color: var(--color-neutral-150);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  dd {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    min-width: 0;
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--color-neutral-white);
  }

  .value {
    overflow: hidden;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .copy {
    flex-shrink: 0;
    padding: 0.125rem 0.5rem;
    font-size: 0.6875rem;
    font-weight: 700;
    color: var(--color-neutral-white);
    background: var(--color-neutral-base);
    border-radius: 100px;
    transition: background-color 0.15s;

    &:hover {
      background: var(--color-neutral-light);
    }

    &.copied {
      color: var(--color-neutral-darkness);
      background: var(--color-brand-plus);
    }
  }

  /* Missing records read as "not yet" (orange), not as an error; they pulse while being checked */
  .status {
    color: ${props => (props.$verified ? "var(--color-feedback-success)" : "var(--color-orange)")};
  }

  .dot {
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    margin: 0 2px;
    background: currentColor;
    border-radius: 100%;
  }

  .checking {
    animation: ${breathe} 1.6s ease-in-out infinite;

    .dot {
      animation: ${ring} 1.6s ease-out infinite;
    }
  }

  /* Full-width line explaining an unrecoverable issue (e.g. Cloudflare's proxy), below the grid */
  .issue {
    grid-column: 1 / -1;
    font-size: 0.75rem;
    line-height: 1.125rem;
    color: var(--color-orange);

    a {
      color: inherit;
      text-decoration: underline;
    }
  }
`;

type Props = {
  records: DnsRecord[];
  /** Shows a spinner on the records still missing while the DNS is being checked. */
  checking?: boolean;
};

export const DnsRecords = ({ records, checking }: Props) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState<string>();

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(current => (current === text ? undefined : current)), 2000);
  };

  const prefix = "pages.settings.customDomain.records";

  const CopyButton = ({ text }: { text: string }) => (
    <button type="button" className={classNames("copy", { copied: copied === text })} onClick={() => copy(text)}>
      {copied === text ? t(`${prefix}.copied`) : t(`${prefix}.copy`)}
    </button>
  );

  return (
    <List>
      {records.map(record => {
        const proxied = !record.verified && record.issue === CLOUDFLARE_PROXY_ISSUE;

        return (
          <Item key={`${record.type}-${record.name}`} $verified={record.verified}>
            <dl style={{ display: "contents" }}>
              <dt>{t(`${prefix}.type`)}</dt>
              <dd>{record.type}</dd>
              <dt>{t(`${prefix}.name`)}</dt>
              <dd>
                <span className="value">{record.name}</span>
                <CopyButton text={record.name} />
              </dd>
              <dt>{t(`${prefix}.value`)}</dt>
              <dd>
                <span className="value">{record.value}</span>
                <CopyButton text={record.value} />
              </dd>
              <dt>{t(`${prefix}.status`)}</dt>
              <dd className={classNames("status", { checking: checking && !record.verified && !proxied })}>
                {record.verified ? (
                  <CheckIcon width={12} height={12} color="currentColor" />
                ) : (
                  <span className="dot" aria-hidden="true" />
                )}
                {record.verified
                  ? t(`${prefix}.verified`)
                  : proxied
                    ? t(`${prefix}.cloudflareProxy`)
                    : checking
                      ? t(`${prefix}.searching`)
                      : t(`${prefix}.notFound`)}
              </dd>
              {proxied && (
                <dd className="issue">
                  <Trans
                    i18nKey={`${prefix}.cloudflareProxyHint`}
                    components={{ anchor: <ExternalLink href={CLOUDFLARE_PROXY_HELP_URL} /> }}
                  />
                </dd>
              )}
            </dl>
          </Item>
        );
      })}
    </List>
  );
};
